// The stripe-js-v3 contains a copy of below codes for loading the Google maps library on merchant's site directly:
// https://git.corp.stripe.com/stripe-internal/stripe-js-v3/blob/master/src/lib/outer/googleMapsService.ts
// When updating this file, consider also updating the corresponding logic in stripe-js-v3.

import type {AppConfig} from '../initialize';
import type {
  GoogleMapsInboundPayload as InboundPayload,
  GoogleMapsOutboundPayload as OutboundPayload,
  PlacesServiceStatus,
} from '../../shared/GoogleMapsTypes';
import {initialize} from '../initialize';
import type {SendFn} from '../setupMessages';

declare global {
  interface Window {
    initGoogleMapsService: () => void;
    initGoogleMapsMapCallback: () => void;
  }
}

const getApiStatus = (statusString: string): PlacesServiceStatus => {
  switch (statusString) {
    case 'INVALID_REQUEST':
    case 'NOT_FOUND':
    case 'OK':
    case 'OVER_QUERY_LIMIT':
    case 'REQUEST_DENIED':
    case 'UNKNOWN_ERROR':
    case 'ZERO_RESULTS':
      return statusString;
    default:
      throw new Error(`Unhandled error type: ${statusString}`);
  }
};

let autocompleteService: google.maps.places.AutocompleteService | null = null;
let placeService: google.maps.places.PlacesService | null = null;

window.initGoogleMapsService = () => {
  const {google} = window;
  let sessionToken = new google.maps.places.AutocompleteSessionToken();
  const placePredictionCache = new Map<
    string,
    google.maps.places.PlacePrediction
  >();
  const statuses = google.maps.places.PlacesServiceStatus;

  // Wraps the API call in an error handler so we can propagate any generic
  // exceptions back to the UI, and disable the autocomplete feature.
  const handlePredictionsExceptions = (
    nonce: string,
    respond: SendFn<'GoogleMaps'>,
    getPredictions: () => void,
  ) => {
    try {
      getPredictions();
    } catch (error) {
      respond({
        tag: 'ADDRESS_AUTOCOMPLETE_PREDICTION_RESULTS',
        value: {
          predictions: [],
          status: 'error',
          error: {
            status: 'UNKNOWN_ERROR',
            message: (error as Error).message,
          },
        },
        ...(nonce && {nonce}),
      });
    }
  };

  // @ts-expect-error - TS2339 - Property 'handleGetPredictions' does not exist on type 'Window & typeof globalThis'.
  window.handleGetPredictions = (
    // @ts-expect-error - TS7031 - Binding element 'search' implicitly has an 'any' type. | TS7031 - Binding element 'countryRestrictions' implicitly has an 'any' type.
    {search, countryRestrictions, useNewPlacesApi},
    respond: SendFn<'GoogleMaps'>,
    nonce: any,
  ) => {
    const deprecatedGetPredictions = () => {
      if (!autocompleteService) {
        autocompleteService = new google.maps.places.AutocompleteService();
      }

      autocompleteService?.getPlacePredictions(
        {
          input: search,
          sessionToken,
          componentRestrictions: {country: countryRestrictions},
        },
        (predictions, status) => {
          switch (status) {
            case statuses.OK:
              respond({
                tag: 'ADDRESS_AUTOCOMPLETE_PREDICTION_RESULTS',
                value: {
                  predictions,
                  status: 'ok',
                },
                ...(nonce && {nonce}),
              });
              break;
            case statuses.ZERO_RESULTS:
              respond({
                tag: 'ADDRESS_AUTOCOMPLETE_PREDICTION_RESULTS',
                value: {
                  predictions: [],
                  status: 'zeroResults',
                },
                ...(nonce && {nonce}),
              } as OutboundPayload);
              break;
            case statuses.INVALID_REQUEST:
            case statuses.OVER_QUERY_LIMIT:
            case statuses.REQUEST_DENIED:
            case statuses.UNKNOWN_ERROR:
              respond({
                tag: 'ADDRESS_AUTOCOMPLETE_PREDICTION_RESULTS',
                value: {
                  predictions: [],
                  status: 'error',
                  error: {
                    status: getApiStatus(status),
                    message: 'Google Maps returned an API error',
                  },
                },
                ...(nonce && {nonce}),
              } as OutboundPayload);
              break;
            default:
              throw new Error(`Unhandled error type: ${status}`);
          }
        },
      );
    };

    const getPredictions = () => {
      // Capture the session token at request initiation so we can detect
      // whether the session was rotated while the request was in flight.
      const requestSessionToken = sessionToken;
      google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: search,
        sessionToken: requestSessionToken,
        includedRegionCodes: countryRestrictions,
      })
        .then(
          (response: {
            suggestions: Array<google.maps.places.AutocompleteSuggestion>;
          }) => {
            const {suggestions} = response;
            const filteredSuggestions = suggestions
              .map((suggestion) => {
                const {placePrediction} = suggestion;
                if (!placePrediction) {
                  return null;
                }
                if (requestSessionToken === sessionToken) {
                  placePredictionCache.set(
                    placePrediction.placeId,
                    placePrediction,
                  );
                }

                return {
                  description: placePrediction.text?.toString(),
                  structured_formatting: {
                    main_text: placePrediction.mainText?.toString() || '',
                    secondary_text:
                      placePrediction.secondaryText?.toString() || '',
                    main_text_matched_substrings:
                      placePrediction.mainText?.matches?.map(
                        ({
                          endOffset,
                          startOffset,
                        }: {
                          endOffset: number;
                          startOffset: number;
                        }) => {
                          return {
                            length: endOffset - startOffset,
                            offset: startOffset,
                          };
                        },
                      ),
                  },
                  place_id: placePrediction.placeId,
                  types: placePrediction.types,
                };
              })
              .filter((suggestion) => suggestion !== null);
            if (filteredSuggestions.length > 0) {
              respond({
                tag: 'ADDRESS_AUTOCOMPLETE_PREDICTION_RESULTS',
                value: {
                  predictions: filteredSuggestions,
                  status: 'ok',
                },
                ...(nonce && {nonce}),
              });
            } else {
              respond({
                tag: 'ADDRESS_AUTOCOMPLETE_PREDICTION_RESULTS',
                value: {
                  predictions: [],
                  status: 'zeroResults',
                },
                ...(nonce && {nonce}),
              } as OutboundPayload);
            }
          },
        )
        // @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
        .catch((e) => {
          respond({
            tag: 'ADDRESS_AUTOCOMPLETE_PREDICTION_RESULTS',
            value: {
              predictions: [],
              status: 'error',
              error: {
                message: e?.message,
              },
            },
            ...(nonce && {nonce}),
          } as OutboundPayload);
        });
    };

    handlePredictionsExceptions(nonce, respond, () => {
      if (useNewPlacesApi) {
        getPredictions();
      } else {
        deprecatedGetPredictions();
      }
    });
  };

  // @ts-expect-error - TS2339 - Property 'handleGetDetails' does not exist on type 'Window & typeof globalThis'.
  window.handleGetDetails = (
    // @ts-expect-error - TS7031 - Binding element 'placeId' implicitly has an 'any' type.
    {placeId, useNewPlacesApi},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    respond: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nonce: any,
  ) => {
    if (useNewPlacesApi) {
      const cachedPrediction = placePredictionCache.get(placeId);
      const newPlaceService = cachedPrediction
        ? cachedPrediction.toPlace()
        : new google.maps.places.Place({id: placeId});
      return (
        newPlaceService
          .fetchFields({fields: ['addressComponents']})
          .then((response: {place: google.maps.places.Place}) => {
            const {place} = response;
            const address_components = place.addressComponents?.map(
              (component: google.maps.places.AddressComponent) => {
                return {
                  long_name: component?.longText?.toString() || '',
                  short_name: component?.shortText?.toString() || '',
                  types: component?.types,
                };
              },
            );
            respond({
              tag: 'ADDRESS_AUTOCOMPLETE_PLACE_DETAILS',
              value: {
                place: {address_components},
              },
              ...(nonce && {nonce}),
            } as OutboundPayload);
          })
          // @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
          .catch((e) => {
            respond({
              tag: 'ADDRESS_AUTOCOMPLETE_PLACE_DETAILS',
              value: {
                error: {
                  message: e?.message,
                },
              },
              ...(nonce && {nonce}),
            } as OutboundPayload);
          })
          .finally(() => {
            sessionToken = new google.maps.places.AutocompleteSessionToken();
            placePredictionCache.clear();
          })
      );
    } else {
      if (!placeService) {
        // Google Maps requires that PlacesService must be initialized with a
        // DOM node (https://stackoverflow.com/questions/14343965/google-places-library-without-map)
        const map = document.createElement('div');
        placeService = new google.maps.places.PlacesService(map);
      }

      placeService?.getDetails(
        {
          placeId,
          sessionToken,
          fields: ['address_components'],
        },
        (place, status) => {
          try {
            if (status !== statuses.OK) {
              respond({
                tag: 'ADDRESS_AUTOCOMPLETE_PLACE_DETAILS',
                value: {
                  error: {
                    status: getApiStatus(status),
                    message: 'Google Maps returned an API error',
                  },
                },
                ...(nonce && {nonce}),
              } as OutboundPayload);

              return;
            }

            respond({
              tag: 'ADDRESS_AUTOCOMPLETE_PLACE_DETAILS',
              value: {
                place,
              },
              ...(nonce && {nonce}),
            } as OutboundPayload);
          } finally {
            sessionToken = new google.maps.places.AutocompleteSessionToken();
            placePredictionCache.clear();
          }
        },
      );
    }
  };
};

const loadMapsAPI = (apiKey: string, callback?: string) => {
  const script = document.createElement('script');
  const callbackName = callback || 'initGoogleMapsService';
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places&callback=${callbackName}`;
  script.async = true;

  if (!document.head) {
    throw new Error(`Unexpectedly missing document head`);
  }

  document.head.appendChild(script);
};

let mapInstance: google.maps.Map | null = null;
let _markersArray: Array<google.maps.Marker> = [];

const initializeMapVisualization = (
  message: InboundPayload & {tag: 'INITIALIZE_MAP'},
  respond: SendFn<'GoogleMaps'>,
  nonce: string | undefined,
  root: HTMLElement,
) => {
  try {
    const {
      apiKey,
      center,
      zoom,
      markers,
      mapTypeId,
      gestureHandling,
      disableDefaultUI,
      zoomControl,
      mapTypeControl,
      scaleControl,
      streetViewControl,
      rotateControl,
      fullscreenControl,
      injectBaseStyles,
      styles,
    } = message.message;

    const initMap = () => {
      try {
        const {google} = window;
        if (!google || !google.maps) {
          respond({
            tag: 'INITIALIZE_MAP_RESULT',
            error: {
              message: 'Google Maps API not loaded',
            },
            ...(nonce && {nonce}),
          } as OutboundPayload);
          return;
        }

        if (injectBaseStyles) {
          const style = document.createElement('style');
          style.textContent = `
            html, body {
              height: 100%;
              margin: 0;
              padding: 0;
            }
            #root {
              height: 100%;
              width: 100%;
            }
          `;
          document.head.appendChild(style);
        }

        mapInstance = new google.maps.Map(root, {
          center,
          zoom,
          mapTypeId: mapTypeId || 'roadmap',
          gestureHandling: gestureHandling || 'auto',
          disableDefaultUI,
          zoomControl,
          mapTypeControl,
          scaleControl,
          streetViewControl,
          rotateControl,
          fullscreenControl,
          styles,
        });

        if (markers && markers.length > 0) {
          _markersArray = markers.map((marker) => {
            const markerInstance = new google.maps.Marker({
              position: marker.location,
              map: mapInstance,
              title: marker.title,
              label: marker.label,
            });

            // Track marker clicks
            markerInstance.addListener('click', () => {
              respond({
                tag: 'MAP_ANALYTICS_EVENT',
                eventType: 'marker_click',
                markerTitle: marker.title,
                zoomLevel: mapInstance?.getZoom(),
                ...(nonce && {nonce}),
              } as OutboundPayload);
            });

            return markerInstance;
          });
        }

        // Track map clicks
        if (mapInstance) {
          mapInstance.addListener('click', () => {
            respond({
              tag: 'MAP_ANALYTICS_EVENT',
              eventType: 'click',
              zoomLevel: mapInstance?.getZoom(),
              ...(nonce && {nonce}),
            } as OutboundPayload);
          });

          // Track zoom changes
          mapInstance.addListener('zoom_changed', () => {
            respond({
              tag: 'MAP_ANALYTICS_EVENT',
              eventType: 'zoom',
              zoomLevel: mapInstance?.getZoom(),
              ...(nonce && {nonce}),
            } as OutboundPayload);
          });

          // Track drag start
          mapInstance.addListener('dragstart', () => {
            respond({
              tag: 'MAP_ANALYTICS_EVENT',
              eventType: 'drag_start',
              zoomLevel: mapInstance?.getZoom(),
              ...(nonce && {nonce}),
            } as OutboundPayload);
          });

          // Track drag end
          mapInstance.addListener('dragend', () => {
            respond({
              tag: 'MAP_ANALYTICS_EVENT',
              eventType: 'drag_end',
              zoomLevel: mapInstance?.getZoom(),
              ...(nonce && {nonce}),
            } as OutboundPayload);
          });
        }

        respond({
          tag: 'INITIALIZE_MAP_RESULT',
          value: {
            success: true,
          },
          ...(nonce && {nonce}),
        } as OutboundPayload);
      } catch (err) {
        respond({
          tag: 'INITIALIZE_MAP_RESULT',
          error: {
            message: `Map initialization error: ${(err as Error).message}`,
          },
          ...(nonce && {nonce}),
        } as OutboundPayload);
      }
    };

    if (window.google && window.google.maps) {
      initMap();
    } else {
      window.initGoogleMapsMapCallback = initMap;
      loadMapsAPI(apiKey, 'initGoogleMapsMapCallback');
    }
  } catch (error) {
    respond({
      tag: 'INITIALIZE_MAP_RESULT',
      error: {
        message: (error as Error).message,
      },
      ...(nonce && {nonce}),
    } as OutboundPayload);
  }
};

const initializeStaticMap = (
  message: {
    tag: 'INITIALIZE_STATIC_GOOGLE_MAP';
    message: {
      apiKey: string;
      center: {lat: number; lng: number};
      zoom: number;
      width?: number;
      height?: number;
      markerColor?: string;
    };
    nonce?: string;
  },
  respond: SendFn<'GoogleMaps'>,
  nonce: string | undefined,
  root: HTMLElement,
) => {
  try {
    const {
      apiKey,
      center,
      zoom,
      width = 600,
      height = 400,
      markerColor = '0xb49cfc',
    } = message.message;

    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';

    const params = new URLSearchParams({
      center: `${center.lat},${center.lng}`,
      zoom: zoom.toString(),
      size: `${width}x${height}`,
      scale: '2',
      key: apiKey,
    });

    params.append(
      'style',
      'feature:all|element:geometry|saturation:-100|lightness:60',
    );
    params.append(
      'style',
      'feature:all|element:labels|saturation:-100|lightness:30',
    );
    params.append('style', 'feature:poi|element:labels|visibility:off');
    params.append(
      'style',
      'feature:administrative.country|element:labels|visibility:off',
    );
    params.append(
      'style',
      'feature:administrative.province|element:labels|visibility:off',
    );
    params.append(
      'style',
      'feature:administrative.neighborhood|element:labels|visibility:off',
    );
    params.append(
      'style',
      'feature:administrative.land_parcel|element:labels|visibility:off',
    );
    params.append('style', 'feature:road|element:labels|visibility:off');
    params.append('style', 'feature:transit|element:labels|visibility:off');

    const colorValue = markerColor.startsWith('#')
      ? `0x${markerColor.slice(1)}`
      : markerColor;
    params.append('markers', `color:${colorValue}|${center.lat},${center.lng}`);

    const mapUrl = `${baseUrl}?${params.toString()}`;

    root.style.position = 'absolute';
    root.style.top = '0';
    root.style.left = '0';

    const img = document.createElement('img');
    img.alt = 'Location map';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.display = 'block';

    img.onload = () => {
      respond({
        tag: 'INITIALIZE_STATIC_GOOGLE_MAP_RESULT',
        value: {
          success: true,
        },
        ...(nonce && {nonce}),
      } as OutboundPayload);
    };

    img.onerror = () => {
      respond({
        tag: 'INITIALIZE_STATIC_GOOGLE_MAP_RESULT',
        error: {
          message: `Failed to load map image from ${mapUrl}`,
        },
        ...(nonce && {nonce}),
      } as OutboundPayload);
    };

    root.appendChild(img);
    img.src = mapUrl;
  } catch (error) {
    respond({
      tag: 'INITIALIZE_STATIC_GOOGLE_MAP_RESULT',
      error: {
        message: (error as Error).message,
      },
      ...(nonce && {nonce}),
    } as OutboundPayload);
  }
};

export const googleMapsHandler = (
  {tag, message, nonce}: InboundPayload,
  respond: SendFn<'GoogleMaps'>,
  root?: HTMLElement,
): any => {
  switch (tag) {
    case 'INITIALIZE_MAPS_API':
      if (message.apiKey) {
        loadMapsAPI(message.apiKey);
      }
      respond({
        tag: 'INITIALIZE_MAPS_API_RESULT',
        ...(nonce && {nonce}),
      });
      break;
    case 'INITIALIZE_MAP':
      if (!root) {
        respond({
          tag: 'INITIALIZE_MAP_RESULT',
          error: {
            message: 'Root element not provided for map initialization',
          },
          ...(nonce && {nonce}),
        } as OutboundPayload);
        return;
      }
      initializeMapVisualization(
        {tag: 'INITIALIZE_MAP', message, nonce},
        respond,
        nonce,
        root,
      );
      break;
    case 'INITIALIZE_STATIC_GOOGLE_MAP':
      if (!root) {
        respond({
          tag: 'INITIALIZE_STATIC_GOOGLE_MAP_RESULT',
          error: {
            message: 'Root element not provided for static map initialization',
          },
          ...(nonce && {nonce}),
        } as OutboundPayload);
        return;
      }
      initializeStaticMap(
        {tag: 'INITIALIZE_STATIC_GOOGLE_MAP', message, nonce},
        respond,
        nonce,
        root,
      );
      break;
    case 'GET_ADDRESS_AUTOCOMPLETE_PREDICTIONS':
      // @ts-expect-error - TS2339 - Property 'handleGetPredictions' does not exist on type 'Window & typeof globalThis'.
      return window.handleGetPredictions(message, respond, nonce);
    case 'GET_ADDRESS_AUTOCOMPLETE_RESULT':
      // @ts-expect-error - TS2339 - Property 'handleGetDetails' does not exist on type 'Window & typeof globalThis'.
      return window.handleGetDetails(message, respond, nonce);
    default:
      throw new Error(`Unhandled request type: ${tag}`);
  }
};

const app = (config: AppConfig<'GoogleMaps'>) => {
  config.subscribe(async (messageData, respond) => {
    googleMapsHandler(messageData, respond, config.root);
  });
};

initialize({
  app,
  owner: 'checkout',
});
